/**
 * StageDocuments — Iter88zk (2026-02-19)
 *
 * Kompakter Uploader + Dokumenten-Liste pro Kanban-Stufe. Ersetzt die
 * Upload-UI aus dem gelöschten BzaWorkflowPanel. Nutzt den in Iter88zj
 * gebauten Multipart-Endpoint (Object Storage), damit große PDFs bis
 * 25 MB durchgehen und der 16-MB-MongoDB-Doc-Limit nicht mehr blockiert.
 */
import { useEffect, useRef, useState } from "react";
import { Paperclip, Upload, Loader2, FileText, Download, ChevronDown, ChevronRight, Send, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const KB_MAX = 25 * 1024; // 25 MB

// Per E-Mail versendete Dokumente dieser Stufe (z. B. Auftragsbestätigung) – öffnen statt herunterladen.
function SentFileRow({ f, stageKey }) {
  return (
    <li className="flex items-center gap-2 px-2 py-1.5 bg-[#00E575]/5 border border-[#00E575]/25 rounded-md text-[12px] text-zinc-200" data-testid={`stage-${stageKey}-sent-${f.key}`}>
      <Send size={12} className="text-[#00E575] flex-shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="font-mono truncate block" title={f.filename}>{f.filename}</span>
        <span className="text-[10.5px] text-zinc-500 block truncate">versendet {f.sublabel}</span>
      </span>
      <button type="button" onClick={f.onOpen} className="h-7 px-2 rounded bg-zinc-800 hover:bg-zinc-700 text-[11px] font-semibold inline-flex items-center gap-1 flex-shrink-0"
        data-testid={`stage-${stageKey}-sent-open-${f.key}`}>
        <Eye size={11} /> Ansehen
      </button>
    </li>
  );
}

export default function StageDocuments({ projectId, stageKey, canUpload, sentFiles = [] }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showAllSent, setShowAllSent] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/stages/${stageKey}/documents`);
      setDocs(r.data?.documents || []);
    } catch (_e) { setDocs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [projectId, stageKey]);// eslint-disable-line react-hooks/exhaustive-deps

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > KB_MAX * 1024) {
      toast.error(`Datei zu groß: ${(file.size/1024/1024).toFixed(1)} MB · max. 25 MB`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(
        `/projects/${projectId}/stages/${stageKey}/documents/upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
      );
      toast.success(`"${file.name}" hochgeladen`);
      setOpen(true);
      await load();
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.detail;
      let msg = serverMsg || "Upload fehlgeschlagen";
      if (status === 413) msg = `Datei zu groß: ${serverMsg || "max. 25 MB"}`;
      else if (!status) msg = "Verbindungsfehler — bitte erneut versuchen";
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const r = await api.get(`/projects/${projectId}/stages/${stageKey}/documents/${doc.id}`);
      const b64 = r.data?.content_base64;
      if (!b64) throw new Error("Kein Inhalt");
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: doc.mime || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename || "download"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    }
  };

  if (!docs.length && !sentFiles.length && !canUpload) return null;
  const total = docs.length + sentFiles.length;

  return (
    <div className="mt-2 border-t border-zinc-800/60 pt-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-zinc-300 inline-flex items-center gap-1"
          data-testid={`stage-${stageKey}-docs-toggle`}
        >
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          <Paperclip size={11} /> Dokumente ({loading ? "…" : total})
        </button>
        {canUpload && (
          <>
            <input
              type="file" ref={fileInputRef} onChange={onFileChange}
              className="hidden" data-testid={`stage-${stageKey}-file-input`}
              accept="application/pdf,image/*,.docx,.doc,.xlsx,.xls,.txt"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="ml-auto h-6 px-2 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold inline-flex items-center gap-1 disabled:opacity-40"
              data-testid={`stage-${stageKey}-upload-btn`}
            >
              {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
              {uploading ? "Hoch …" : "PDF/Foto"}
            </button>
          </>
        )}
      </div>
      {open && total > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`stage-${stageKey}-docs-list`}>
          {(showAllSent ? sentFiles : sentFiles.slice(0, 5)).map((f) => <SentFileRow key={f.key} f={f} stageKey={stageKey} />)}
          {sentFiles.length > 5 && (
            <li>
              <button type="button" onClick={() => setShowAllSent((v) => !v)} data-testid={`stage-${stageKey}-sent-more`}
                className="text-[11.5px] text-[#00E575] hover:underline px-2 py-1">
                {showAllSent ? "Weniger anzeigen" : `Alle ${sentFiles.length} versendeten Dateien anzeigen`}
              </button>
            </li>
          )}
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-2 px-2 py-1.5 bg-zinc-900/60 border border-zinc-800 rounded-md text-[12px] text-zinc-300">
              <FileText size={12} className="text-zinc-500 flex-shrink-0" />
              <span className="font-mono truncate flex-1 min-w-0" title={d.filename}>{d.filename}</span>
              <span className="text-[10.5px] text-zinc-500 font-mono flex-shrink-0">
                {d.size_bytes ? `${(d.size_bytes/1024).toFixed(0)} KB` : ""}
              </span>
              <button
                type="button"
                onClick={() => download(d)}
                className="p-1 hover:bg-zinc-800 rounded"
                title={`${d.uploaded_by_name || ""} · ${d.uploaded_at?.slice(0, 10) || ""}`}
                data-testid={`stage-doc-download-${d.id}`}
              >
                <Download size={12} className="text-zinc-400 hover:text-zinc-200" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
